import { useState, useRef, useMemo, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDecks } from '../../hooks/useDecks'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'
import api, { extractErrorMessage } from '../../api/client'
import { toast } from './Toast'
import type { Deck } from '../../types'
import { useLocale } from '../../i18n/context'
import type { AppTranslations } from '../../i18n/app'

const NEW_DECK_SENTINEL = '__new__'

/** Roles a column may be assigned. Image and Audio are deliberately absent —
 *  they are not assignable at import (ADR-075 Decision 5). */
type ColumnRole = 'Term' | 'Translation' | 'Definition' | 'Example' | 'Custom' | 'skip'
type Step = 'file' | 'mapping' | 'result'

/** One column's assignment. Columns are identified by their index in this
 *  array — their position in the parsed file — never by their name, which is
 *  not unique, not always present, and not stable. */
interface ColumnAssignment {
  role: ColumnRole
  customLabel: string
}

/** What the server's preview endpoint reports back. Every determination it
 *  made is here so the surface can show it and offer it back as a control. */
interface ParsePreview {
  encoding: string
  delimiter: string
  delimiter_detected: boolean
  has_header: boolean
  has_header_detected: boolean
  columns: string[]
  rows: string[][]
  row_count: number
}

interface ParseFailure {
  detail: string
  best_delimiter?: string
  disagreeing_count?: number
  disagreeing_rows?: number[]
  likely_cause?: string
}

interface ImportResult {
  imported: number
  skipped: number
  problems?: { row: number; reason: string }[]
  errors?: string[]
}

interface Props {
  isOpen: boolean
  onClose: () => void
  /** Pre-selects this deck as the target — the user may still change it. */
  deckId?: string
  deckTitle?: string
  /** A file already picked outside this modal (e.g. the text-import page's
   *  drop zone). When set, parsing starts immediately on open instead of
   *  showing the file-selection step — no target is pre-selected. */
  initialFile?: File
  onSuccess?: () => void
}

// ── Role pre-fill from header names ───────────────────────────────────────────
// A starting point the author sees and can change, never an authority
// (ADR-075 Decision 1). Only consulted when a header row is in effect.

// Spanish and German words are written without accents or umlauts because the
// comparison drops them and writes "ß" as "ss" (`foldHeader`): "Término" and
// "termino", "Übersetzung" and "ubersetzung" are the same header. German also
// lists the "ae"/"ue" spellings ("Uebersetzung"). Russian is compared exactly as
// written. Mirrors the backend's header vocabulary
// (csv_import_parsing._FIELD_WORDS_BY_LANGUAGE); a later language adds its words
// to these sets.
const TERM_SYNONYMS = new Set([
  'term', 'word', 'front', 'question', 'vocab', 'vocabulary', 'headword',
  'termino', 'palabra', 'vocabulario',
  'begriff', 'wort', 'vokabel', 'ausdruck', 'stichwort',
])
const DEF_SYNONYMS = new Set([
  'definition', 'def', 'meaning', 'back', 'description', 'gloss',
  'definicion', 'significado', 'descripcion',
  'bedeutung', 'erklarung', 'erklaerung', 'beschreibung',
])
const EXAMPLE_SYNONYMS = new Set([
  'example', 'sentence', 'sample', 'usage', 'context', 'examples',
  'ejemplo', 'ejemplos', 'frase', 'oracion', 'uso', 'contexto',
  'beispiel', 'beispiele', 'beispielsatz', 'satz', 'kontext', 'verwendung',
])
const TRANSLATION_ROOTS = [
  'translation', 'перевод', 'translate', 'trans', 'traduccion', 'ubersetzung', 'uebersetzung',
]

const CYRILLIC = /[Ѐ-ӿ]/

/** Lower-case and, for non-Cyrillic text, drop accents and umlauts and write
 *  "ß" as "ss". Cyrillic is left as written, so "й" is never folded to "и". */
function foldHeader(header: string): string {
  const lower = header.toLowerCase().trim()
  if (CYRILLIC.test(lower)) return lower
  return lower.normalize('NFD').replace(/[\u0300-\u036f]/g, '').normalize('NFC').replace(/ß/g, 'ss')
}

function roleFromHeader(header: string): ColumnRole | null {
  const lower = foldHeader(header)
  if (!lower) return null
  if (TERM_SYNONYMS.has(lower)) return 'Term'
  if (DEF_SYNONYMS.has(lower)) return 'Definition'
  if (EXAMPLE_SYNONYMS.has(lower)) return 'Example'
  if (TRANSLATION_ROOTS.some(r => lower === r || lower.startsWith(r + '_') || lower.endsWith('_' + r))) {
    return 'Translation'
  }
  return null
}

/**
 * Build the initial assignments for a freshly parsed file.
 *
 * Position 0 is pre-assigned Term because that is what most files look like —
 * but it is an ordinary assignment the author can change like any other, and a
 * header name that names a known field type wins over it, so a file whose
 * first column is headed "Translation" does not start out mis-assigned.
 */
function initialAssignments(columns: string[], hasHeader: boolean): ColumnAssignment[] {
  const named: (ColumnRole | null)[] = columns.map(c => (hasHeader ? roleFromHeader(c) : null))
  const assignments: ColumnAssignment[] = named.map(role => ({
    role: role ?? 'skip',
    customLabel: '',
  }))

  // Exactly one Term: keep the first header-named Term if there is one,
  // otherwise fall back to position 0.
  const namedTermIndex = named.findIndex(r => r === 'Term')
  named.forEach((r, i) => {
    if (r === 'Term' && i !== namedTermIndex) assignments[i].role = 'skip'
  })
  if (namedTermIndex === -1 && assignments.length > 0) {
    assignments[0] = { role: 'Term', customLabel: '' }
  }
  return assignments
}

// ── Validation — derived, never stored ───────────────────────────────────────

interface Validation {
  blocking: string[]
  /** Non-blocking: cards will be created but will not enter learning yet. */
  noPromptMaterial: boolean
  canImport: boolean
}

/**
 * The single derivation the error banner, the preview table and the confirming
 * action all read. Matches the contract exactly: exactly one Term, a Custom
 * column needs a name, and nothing else is required.
 */
function validate(
  assignments: ColumnAssignment[],
  columnName: (index: number) => string,
  csv: Pick<AppTranslations['csvImport'], 'termRequiredNamed' | 'termTooManyNamed' | 'customNameRequired'>,
): Validation {
  const blocking: string[] = []

  const termIndexes = assignments
    .map((a, i) => (a.role === 'Term' ? i : -1))
    .filter(i => i >= 0)
  if (termIndexes.length === 0) blocking.push(csv.termRequiredNamed)
  else if (termIndexes.length > 1) {
    blocking.push(csv.termTooManyNamed(termIndexes.map(columnName).join(', ')))
  }

  assignments.forEach((a, i) => {
    if (a.role === 'Custom' && !a.customLabel.trim()) {
      blocking.push(csv.customNameRequired(columnName(i)))
    }
  })

  const noPromptMaterial = !assignments.some(
    a => a.role === 'Definition' || a.role === 'Translation',
  )

  return { blocking, noPromptMaterial, canImport: blocking.length === 0 }
}

/** Serialise to the position-keyed mapping contract. */
function toColumnMapping(assignments: ColumnAssignment[]): Record<string, unknown[]> {
  const mapping: Record<string, unknown[]> = {}
  assignments.forEach((a, i) => {
    mapping[String(i)] =
      a.role === 'skip'
        ? ['skip']
        : a.role === 'Custom'
          ? [{ role: 'Custom', label: a.customLabel.trim() }]
          : [a.role]
  })
  return mapping
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CSVImportModal({ isOpen, onClose, deckId, deckTitle, initialFile, onSuccess }: Props) {
  const qc = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { tr } = useLocale()
  const csv = tr.csvImport
  // ADR-106 P3: no top-level swipe while the import modal is open. Before the
  // early `if (!isOpen) return null` below, as every hook must be.
  useSwipeNavBlock(isOpen, 'csv-import-modal')

  const [step, setStep] = useState<Step>('file')
  // Default is a new deck; reaching the surface from inside an existing deck
  // pre-selects that deck instead. Either way it is an ordinary control the
  // user can change — never a fixed target.
  const [selectedDeckId, setSelectedDeckId] = useState<string>(deckId ?? NEW_DECK_SENTINEL)
  const [newDeckName, setNewDeckName] = useState('')
  const [addToQueue, setAddToQueue] = useState(true)
  const [file, setFile] = useState<File | null>(null)

  // Parsing state. `preview` holds the server's determinations; `delimiter`
  // and `hasHeader` hold the author's overrides (null = "as detected"). The
  // values SENT at commit are read off `preview`, so what the table showed and
  // what the import runs with cannot drift apart.
  const [preview, setPreview] = useState<ParsePreview | null>(null)
  const [parseFailure, setParseFailure] = useState<ParseFailure | null>(null)
  const [reparsing, setReparsing] = useState(false)

  const [assignments, setAssignments] = useState<ColumnAssignment[]>([])
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [dragError, setDragError] = useState<string | null>(null)
  const dragCounterRef = useRef(0)

  const { data: decksData } = useDecks(1, 100)

  const columnName = useMemo(() => {
    return (index: number) => {
      const raw = preview?.columns[index]?.trim()
      return raw || csv.columnLabel(index + 1)
    }
  }, [preview, csv])

  const validation = useMemo(
    () => validate(assignments, columnName, csv),
    [assignments, columnName, csv],
  )

  // A file already selected outside this modal (text-import's drop zone)
  // starts parsing immediately — the file-selection step is skipped, and no
  // target is pre-selected (initialFile is only ever passed without deckId).
  // `processFile` is a hoisted function declaration, so this can call it even
  // though it is defined later in the component body.
  useEffect(() => {
    if (initialFile) processFile(initialFile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile])

  // The drop zone below handles its own drop, but a file released anywhere
  // else while this modal is open — the backdrop, the header, past the
  // window entirely — would otherwise hit the browser's default handler and
  // navigate the tab to display the file. Suppress that for as long as the
  // modal is open; the drop zone's own handler already stops propagation for
  // drops inside it, so this only ever catches drops the drop zone didn't.
  useEffect(() => {
    if (!isOpen) return
    const preventDefault = (e: DragEvent) => e.preventDefault()
    window.addEventListener('dragover', preventDefault)
    window.addEventListener('drop', preventDefault)
    return () => {
      window.removeEventListener('dragover', preventDefault)
      window.removeEventListener('drop', preventDefault)
    }
  }, [isOpen])

  if (!isOpen) return null

  /**
   * Ask the server to parse the file, optionally under an override. Called on
   * open and again whenever the author changes the delimiter or the header
   * decision — the surface never parses the file itself, so the preview table
   * and the import always come from the same parser.
   */
  async function runPreview(
    f: File,
    overrides?: { delimiter?: string; hasHeader?: boolean },
    keepAssignments?: ColumnAssignment[],
  ) {
    setReparsing(true)
    setParseFailure(null)
    try {
      const form = new FormData()
      form.append('file', f)
      if (overrides?.delimiter !== undefined) form.append('delimiter', overrides.delimiter)
      if (overrides?.hasHeader !== undefined) form.append('has_header', String(overrides.hasHeader))

      const { data } = await api.post<ParsePreview>('/api/v1/decks/import-csv/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setPreview(data)
      // Re-parsing can change the column count; keep assignments only when the
      // shape still matches, otherwise re-derive from the new columns.
      if (keepAssignments && keepAssignments.length === data.columns.length) {
        setAssignments(keepAssignments)
      } else {
        setAssignments(initialAssignments(data.columns, data.has_header))
      }
      setStep('mapping')
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } } | undefined)?.response?.data?.detail
      if (detail && typeof detail === 'object') {
        setParseFailure(detail as ParseFailure)
        setPreview(null)
        setStep('mapping')
      } else {
        setDragError(extractErrorMessage(err, csv.importFailedFallback))
        setStep('file')
      }
    } finally {
      setReparsing(false)
    }
  }

  function processFile(f: File) {
    setFile(f)
    setResult(null)
    setDragError(null)
    setPreview(null)
    setParseFailure(null)
    // Pre-fill the new-deck name regardless of which target is currently
    // selected — the name field is only shown/used when the target is "new
    // deck", and switching to it later should not find a blank field.
    if (!newDeckName.trim()) {
      setNewDeckName(f.name.replace(/\.csv$/i, '').replace(/_/g, ' '))
    }
    void runPreview(f)
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (f) processFile(f)
  }

  function handleDragEnter(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault(); e.stopPropagation()
    dragCounterRef.current++
    setIsDragOver(true)
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault(); e.stopPropagation()
    e.dataTransfer.dropEffect = 'copy'
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault(); e.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) { setIsDragOver(false); setDragError(null) }
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault(); e.stopPropagation()
    dragCounterRef.current = 0
    setIsDragOver(false)
    const dropped = e.dataTransfer.files[0]
    if (!dropped) return
    const nameLower = dropped.name.toLowerCase()
    const isCSV = nameLower.endsWith('.csv') || dropped.type === 'text/csv' || dropped.type === 'application/vnd.ms-excel'
    if (!isCSV) { setDragError(csv.onlyCsvSupported); return }
    processFile(dropped)
  }

  function handleDelimiterChange(next: string) {
    if (!file) return
    // Header decision travels along so one override does not silently reset
    // the other; assignments are kept only if the new parse has the same shape.
    void runPreview(file, { delimiter: next, hasHeader: preview?.has_header })
  }

  function handleHeaderChange(next: boolean) {
    if (!file) return
    void runPreview(file, { delimiter: preview?.delimiter, hasHeader: next }, assignments)
  }

  async function handleImport() {
    // Target resolution reads `selectedDeckId` alone — it is an ordinary
    // control on this screen now, not something fixed by how the surface was
    // reached. A new deck is created here, at confirm time, and nowhere
    // earlier: an abandoned import leaves nothing behind.
    const isNew = selectedDeckId === NEW_DECK_SENTINEL
    if (!file || !preview || !validation.canImport) return
    if (isNew && !newDeckName.trim()) return
    if (!isNew && !selectedDeckId) return

    setImporting(true)
    let createdDeckId: string | null = null

    try {
      if (isNew) {
        const { data: newDeck } = await api.post<Deck>('/api/v1/decks/', {
          title: newDeckName.trim(),
          is_active_for_learning: addToQueue,
        })
        createdDeckId = newDeck.id
      }

      const finalDeckId = createdDeckId ?? selectedDeckId
      const form = new FormData()
      form.append('file', file)
      form.append('column_mapping', JSON.stringify(toColumnMapping(assignments)))
      // Sent from the same object the preview table rendered, so a value the
      // author overrode reaches the import unchanged.
      form.append('delimiter', preview.delimiter)
      form.append('has_header', String(preview.has_header))

      const { data } = await api.post<ImportResult>(
        `/api/v1/decks/${finalDeckId}/import-csv`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )

      setResult(data)
      setStep('result')
      qc.invalidateQueries({ queryKey: ['cards', finalDeckId] })
      qc.invalidateQueries({ queryKey: ['deck', finalDeckId] })
      qc.invalidateQueries({ queryKey: ['decks'] })
      if (isNew) qc.invalidateQueries({ queryKey: ['queue-composition'] })
      onSuccess?.()
    } catch (err) {
      if (createdDeckId) {
        try {
          await api.delete(`/api/v1/decks/${createdDeckId}`)
          qc.invalidateQueries({ queryKey: ['decks'] })
        } catch { /* best-effort rollback */ }
      }
      toast.error(extractErrorMessage(err, csv.importFailedFallback))
    } finally {
      setImporting(false)
    }
  }

  function resetToFileStep() {
    setStep('file')
    setFile(null)
    setPreview(null)
    setParseFailure(null)
    setAssignments([])
    setResult(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleClose() {
    resetToFileStep()
    setDragError(null)
    setIsDragOver(false)
    dragCounterRef.current = 0
    setSelectedDeckId(deckId ?? NEW_DECK_SENTINEL)
    setNewDeckName('')
    setAddToQueue(true)
    onClose()
  }

  const decks = decksData?.items ?? []
  const isCreatingNew = selectedDeckId === NEW_DECK_SENTINEL
  // The resolved target — always computed, never left for the user to leave
  // the screen to decide. deckTitle is the fallback for the deck this modal
  // was opened from, in case it isn't in the first page of `decks`.
  const resolvedTargetTitle = isCreatingNew
    ? newDeckName.trim()
    : (decks.find(d => d.id === selectedDeckId)?.title
        ?? (selectedDeckId === deckId ? deckTitle : undefined)
        ?? '')
  const hasTarget = isCreatingNew ? !!newDeckName.trim() : !!selectedDeckId

  const modalWidth = step === 'mapping' ? 860 : 520

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={handleClose}
    >
      <div
        style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)', padding: 32, maxWidth: modalWidth, width: '100%', maxHeight: '90vh', overflowY: 'auto', transition: 'max-width 0.2s' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {step === 'mapping' && (
              <div style={{ display: 'flex', gap: 4 }}>
                <StepDot active={false} done />
                <StepDot active />
              </div>
            )}
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
              {step === 'file' ? csv.title : step === 'mapping' ? csv.mapColumnsTitle : csv.importCompleteTitle}
            </h2>
          </div>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 22, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        {step === 'result' && result ? (
          <ResultScreen result={result} onClose={handleClose} onImportMore={resetToFileStep} />
        ) : step === 'mapping' ? (
          <MappingStep
            file={file!}
            preview={preview}
            parseFailure={parseFailure}
            reparsing={reparsing}
            assignments={assignments}
            columnName={columnName}
            validation={validation}
            onAssignmentChange={(index, next) =>
              setAssignments(prev => prev.map((a, i) => (i === index ? next : a)))
            }
            onDelimiterChange={handleDelimiterChange}
            onHeaderChange={handleHeaderChange}
            decks={decks}
            selectedDeckId={selectedDeckId}
            onSelectDeck={setSelectedDeckId}
            isCreatingNew={isCreatingNew}
            newDeckName={newDeckName}
            onNewDeckName={setNewDeckName}
            addToQueue={addToQueue}
            onAddToQueue={setAddToQueue}
            hasTarget={hasTarget}
            resolvedTargetTitle={resolvedTargetTitle}
            importing={importing}
            onBack={resetToFileStep}
            onImport={handleImport}
          />
        ) : (
          <FileStep
            isDragOver={isDragOver}
            dragError={dragError}
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClose={handleClose}
          />
        )}
      </div>
    </div>
  )
}

// ── Step 1: File selection ────────────────────────────────────────────────────
// The target deck is chosen on the mapping step, not here — deciding where the
// cards go is an ordinary control alongside the other determinations, not a
// gate the file can't be read without passing first.

function FileStep({
  isDragOver, dragError, fileInputRef, onFileChange, onDragEnter, onDragOver, onDragLeave, onDrop, onClose,
}: {
  isDragOver: boolean
  dragError: string | null
  fileInputRef: React.RefObject<HTMLInputElement | null>
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onDragEnter: (e: React.DragEvent<HTMLDivElement>) => void
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void
  onClose: () => void
}) {
  const { tr } = useLocale()
  const csv = tr.csvImport
  // Dragging a file has no meaning on a touch surface — there is no cursor to
  // carry it. Keep the picker working unchanged; just don't show drag copy
  // or bind drag handlers nothing will ever fire for.
  const isTouch =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  return (
    <>
      <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 20, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
        <strong style={{ color: 'var(--text-primary)' }}>Any CSV works.</strong>{' '}
        You'll choose what each column means and where the cards go in the next
        step, and can change how the file was read. Max 5 MB / 5,000 rows.
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>{csv.csvFileLabel}</label>
        <div
          onClick={() => fileInputRef.current?.click()}
          {...(isTouch ? {} : { onDragEnter, onDragOver, onDragLeave, onDrop })}
          style={{
            border: `2px dashed ${isDragOver ? (dragError ? 'var(--danger)' : 'var(--accent)') : 'var(--border)'}`,
            borderRadius: 'var(--radius-sm)', padding: '28px 16px', textAlign: 'center',
            cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
            background: isDragOver ? (dragError ? 'var(--danger-soft)' : 'var(--accent-soft)') : 'var(--bg-secondary)',
          }}
          onMouseEnter={e => { if (!isDragOver) e.currentTarget.style.borderColor = 'var(--accent)' }}
          onMouseLeave={e => { if (!isDragOver) e.currentTarget.style.borderColor = 'var(--border)' }}
        >
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onFileChange} />
          {isDragOver ? (
            <>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{dragError ? '🚫' : '📥'}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: dragError ? 'var(--danger)' : 'var(--accent)' }}>{dragError ?? csv.dropHere}</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 24, marginBottom: 8 }}>📂</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{isTouch ? csv.tapToChoose : csv.dragOrClick}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', opacity: 0.7, marginTop: 6 }}>
                {csv.columnMappingNext}
              </div>
            </>
          )}
        </div>
        {dragError && !isDragOver && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)' }}>{dragError}</div>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={cancelBtnStyle}>{csv.cancel}</button>
      </div>
    </>
  )
}

// ── Step 2: Parsing summary + column mapping ──────────────────────────────────

function MappingStep({
  file, preview, parseFailure, reparsing, assignments, columnName, validation,
  onAssignmentChange, onDelimiterChange, onHeaderChange,
  decks, selectedDeckId, onSelectDeck, isCreatingNew, newDeckName, onNewDeckName,
  addToQueue, onAddToQueue,
  hasTarget, resolvedTargetTitle, importing, onBack, onImport,
}: {
  file: File
  preview: ParsePreview | null
  parseFailure: ParseFailure | null
  reparsing: boolean
  assignments: ColumnAssignment[]
  columnName: (index: number) => string
  validation: Validation
  onAssignmentChange: (index: number, next: ColumnAssignment) => void
  onDelimiterChange: (next: string) => void
  onHeaderChange: (next: boolean) => void
  decks: Deck[]
  selectedDeckId: string
  onSelectDeck: (val: string) => void
  isCreatingNew: boolean
  newDeckName: string
  onNewDeckName: (val: string) => void
  addToQueue: boolean
  onAddToQueue: (val: boolean) => void
  hasTarget: boolean
  resolvedTargetTitle: string
  importing: boolean
  onBack: () => void
  onImport: () => void
}) {
  const { tr } = useLocale()
  const csv = tr.csvImport

  const ROLE_OPTIONS: { value: ColumnRole; label: string }[] = [
    { value: 'skip',        label: csv.roleSkip },
    { value: 'Term',        label: csv.roleTerm },
    { value: 'Translation', label: csv.roleTranslation },
    { value: 'Definition',  label: csv.roleDefinition },
    { value: 'Example',     label: csv.roleExample },
    { value: 'Custom',      label: csv.roleCustom },
  ]

  const roleColor: Record<ColumnRole, string> = {
    Term: 'var(--success)',
    Definition: 'var(--accent)',
    Example: 'var(--warning)',
    Translation: '#9b59b6',
    Custom: 'var(--text-secondary)',
    skip: 'var(--border)',
  }

  // The delimiter control stays available even when parsing failed — it is the
  // way out of a file that cannot be read as a table.
  const currentDelimiter = preview?.delimiter ?? parseFailure?.best_delimiter ?? ','

  const isEncodingFailure =
    !!parseFailure && parseFailure.disagreeing_count === undefined

  return (
    <div>
      {/* File info bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', marginBottom: 14, fontSize: 13, border: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 16 }}>📄</span>
        <span style={{ fontWeight: 600, color: 'var(--text-primary)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
        <span style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>{(file.size / 1024).toFixed(1)} KB</span>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--accent)', padding: 0, fontFamily: 'var(--font-ui)', flexShrink: 0 }}
        >
          {csv.changeFile}
        </button>
      </div>

      {/* Target — an ordinary control alongside the parsing determinations.
          Changing it never re-reads or re-parses the file: columns, roles,
          overrides and Custom names are untouched by a target switch. */}
      <div style={{
        padding: '10px 12px', marginBottom: 14,
        background: 'var(--bg-secondary)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
      }}>
        <label htmlFor="csv-import-target-deck" style={{ ...labelStyle, marginBottom: 6 }}>{csv.targetDeckLabel}</label>
        <select id="csv-import-target-deck" value={selectedDeckId} onChange={e => onSelectDeck(e.target.value)} style={selectStyle}>
          <option value={NEW_DECK_SENTINEL}>{csv.createNewDeck}</option>
          {decks.length > 0 && <option disabled style={{ color: 'var(--text-secondary)', fontSize: 11 }}>──────────────</option>}
          {decks.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>
        {isCreatingNew && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              type="text" placeholder={csv.deckNamePlaceholder}
              value={newDeckName} onChange={e => onNewDeckName(e.target.value)}
              style={{ ...selectStyle, cursor: 'text', appearance: 'none', border: newDeckName.trim() ? '1px solid var(--border)' : '1px solid var(--danger)' }}
            />
            {!newDeckName.trim() && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{csv.deckNameRequired}</div>}
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontSize: 13, color: 'var(--text-secondary)', minHeight: 44 }}
              onClick={() => onAddToQueue(!addToQueue)}
            >
              <div
                aria-hidden="true"
                style={{
                  width: 32, height: 18, borderRadius: 9, flexShrink: 0,
                  background: addToQueue ? 'var(--accent)' : 'var(--border)',
                  position: 'relative', transition: 'background 0.2s',
                }}
              >
                <div style={{
                  position: 'absolute', top: 3, left: addToQueue ? 13 : 3,
                  width: 12, height: 12, borderRadius: '50%', background: 'white',
                  transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                }} />
              </div>
              {csv.addToQueueToggle}
            </label>
          </div>
        )}
      </div>

      {/* Parsing determinations — stated, and each one changeable */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        padding: '10px 12px', marginBottom: 14,
        background: 'var(--bg-secondary)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text-secondary)',
      }}>
        <span style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
          {csv.parsedAs}
        </span>

        {preview && (
          <span>
            {csv.encodingLabel}: <strong style={{ color: 'var(--text-primary)' }}>{preview.encoding}</strong>
          </span>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {csv.delimiterLabel}:
          <select
            value={currentDelimiter}
            disabled={reparsing}
            onChange={e => onDelimiterChange(e.target.value)}
            style={{
              padding: '4px 6px', fontSize: 12, background: 'var(--bg-surface)',
              border: '1px solid var(--border)', borderRadius: 4,
              color: 'var(--text-primary)', fontFamily: 'var(--font-ui)',
              cursor: reparsing ? 'wait' : 'pointer', minHeight: 32,
            }}
          >
            <option value=",">{csv.delimiterComma}</option>
            <option value=";">{csv.delimiterSemicolon}</option>
            <option value={'\t'}>{csv.delimiterTab}</option>
          </select>
        </label>

        {preview && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: reparsing ? 'wait' : 'pointer', minHeight: 32 }}>
            <input
              type="checkbox"
              checked={preview.has_header}
              disabled={reparsing}
              onChange={e => onHeaderChange(e.target.checked)}
              style={{ cursor: reparsing ? 'wait' : 'pointer', width: 16, height: 16 }}
            />
            {csv.headerRowToggle}
          </label>
        )}

        {preview && (
          <span style={{ marginLeft: 'auto', color: 'var(--text-primary)', fontWeight: 600 }}>
            {reparsing ? csv.reparsing : csv.rowCount(preview.row_count)}
          </span>
        )}
      </div>

      {/* Parsing could not produce a consistent table — say what and why, and
          leave the delimiter control above as the way out. No table is shown,
          because a table here would show empty cells where data was lost. */}
      {parseFailure && (
        <div style={{
          background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
          border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)',
          padding: '12px 14px', marginBottom: 14, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 700, color: 'var(--danger)', marginBottom: 6 }}>
            {isEncodingFailure ? csv.encodingFailed : csv.parseFailedTitle}
          </div>
          {!isEncodingFailure && (
            <>
              <div>
                {csv.parseFailedRows(
                  parseFailure.disagreeing_count ?? 0,
                  (parseFailure.disagreeing_rows ?? []).join(', '),
                )}
              </div>
              {parseFailure.likely_cause && (
                <div style={{ marginTop: 4, color: 'var(--text-secondary)' }}>{parseFailure.likely_cause}</div>
              )}
              <div style={{ marginTop: 6 }}>{csv.parseFailedTryDelimiter}</div>
            </>
          )}
        </div>
      )}

      {preview && (
        <>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {preview.has_header ? csv.headerRowOnHint : csv.headerRowOffHint}
          </p>

          {/* Mapping + preview table. One <th> per column POSITION, so two
              columns sharing a name — or having none — are independent. */}
          <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', marginBottom: 14, opacity: reparsing ? 0.5 : 1, transition: 'opacity 0.15s' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border)' }}>
                  {preview.columns.map((_header, ci) => {
                    const assignment = assignments[ci] ?? { role: 'skip' as ColumnRole, customLabel: '' }
                    const isSkipped = assignment.role === 'skip'
                    const accentColor = roleColor[assignment.role]
                    const needsName = assignment.role === 'Custom' && !assignment.customLabel.trim()
                    return (
                      <th
                        key={ci}
                        style={{
                          padding: '10px 10px 8px', textAlign: 'left', verticalAlign: 'top',
                          minWidth: 150, maxWidth: 210,
                          borderRight: '1px solid var(--border)',
                          opacity: isSkipped ? 0.5 : 1, transition: 'opacity 0.15s',
                        }}
                      >
                        <div style={{
                          fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
                          textTransform: 'uppercase', letterSpacing: '0.05em',
                          marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }} title={columnName(ci)}>
                          {columnName(ci)}
                        </div>
                        <select
                          value={assignment.role}
                          onChange={e => onAssignmentChange(ci, {
                            role: e.target.value as ColumnRole,
                            // Pre-fill a Custom name from this column's header.
                            customLabel:
                              e.target.value === 'Custom' && !assignment.customLabel
                                ? (preview.columns[ci] ?? '').trim()
                                : assignment.customLabel,
                          })}
                          style={{
                            width: '100%', padding: '5px 7px', fontSize: 12, minHeight: 32,
                            background: isSkipped
                              ? 'var(--bg-secondary)'
                              : `color-mix(in srgb, ${accentColor} 12%, var(--bg-surface))`,
                            border: `1px solid ${isSkipped ? 'var(--border)' : accentColor}`,
                            borderRadius: 4, color: 'var(--text-primary)',
                            cursor: 'pointer', fontFamily: 'var(--font-ui)',
                            fontWeight: isSkipped ? 400 : 600, outline: 'none',
                          }}
                        >
                          {ROLE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        {assignment.role === 'Custom' && (
                          <input
                            type="text"
                            value={assignment.customLabel}
                            placeholder={csv.customNamePlaceholder}
                            onChange={e => onAssignmentChange(ci, { ...assignment, customLabel: e.target.value })}
                            style={{
                              width: '100%', marginTop: 5, padding: '5px 7px', fontSize: 12, minHeight: 32,
                              background: 'var(--bg-surface)',
                              border: `1px solid ${needsName ? 'var(--danger)' : 'var(--border)'}`,
                              borderRadius: 4, color: 'var(--text-primary)',
                              fontFamily: 'var(--font-ui)', outline: 'none',
                            }}
                          />
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {preview.rows.length > 0 ? preview.rows.slice(0, 4).map((row, ri) => (
                  <tr key={ri} style={{ borderBottom: ri < Math.min(preview.rows.length, 4) - 1 ? '1px solid var(--border)' : 'none' }}>
                    {preview.columns.map((_, ci) => {
                      const assignment = assignments[ci] ?? { role: 'skip' as ColumnRole, customLabel: '' }
                      const cell = row[ci] ?? ''
                      return (
                        <td
                          key={ci}
                          style={{
                            padding: '7px 10px', verticalAlign: 'top',
                            borderRight: '1px solid var(--border)',
                            opacity: assignment.role === 'skip' ? 0.3 : 1,
                            transition: 'opacity 0.15s',
                            color: assignment.role === 'Term' ? 'var(--text-primary)' : 'var(--text-secondary)',
                            fontWeight: assignment.role === 'Term' ? 600 : 400,
                            maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                          title={cell}
                        >
                          {cell || <span style={{ fontStyle: 'italic', opacity: 0.4 }}>{csv.emptyCellPlaceholder}</span>}
                        </td>
                      )
                    })}
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={preview.columns.length} style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
                      {csv.noDataRows}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Blocking problems — same derivation the confirm button reads */}
          {validation.blocking.length > 0 && (
            <div style={{
              background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
              border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)',
              padding: '8px 12px', marginBottom: 14, fontSize: 12, color: 'var(--danger)', lineHeight: 1.6,
            }}>
              {validation.blocking.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}

          {/* Non-blocking: valid cards that simply do not enter learning yet */}
          {validation.noPromptMaterial && (
            <div style={{
              background: 'color-mix(in srgb, var(--warning) 10%, transparent)',
              border: '1px solid var(--warning)', borderRadius: 'var(--radius-sm)',
              padding: '8px 12px', marginBottom: 14, fontSize: 12,
              color: 'var(--text-primary)', lineHeight: 1.6,
            }}>
              {csv.noPromptMaterialNotice}
            </div>
          )}
        </>
      )}

      {/* Actions — the confirming action always states the resolved target,
          so there is never a state where the file is parsed and the import
          is blocked pending a choice the user must leave this screen to make. */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={onBack} style={cancelBtnStyle}>{csv.backBtn}</button>
        <button
          onClick={onImport}
          disabled={!validation.canImport || !preview || !hasTarget || importing || reparsing}
          style={{
            padding: '10px 24px',
            background: validation.canImport && preview && hasTarget && !importing && !reparsing ? 'var(--accent-fill)' : 'var(--border)',
            color: validation.canImport && preview && hasTarget && !importing && !reparsing ? 'var(--text-on-accent)' : 'var(--text-secondary)',
            border: 'none', borderRadius: 'var(--radius-pill)',
            fontSize: 14, fontWeight: 600,
            cursor: validation.canImport && preview && hasTarget && !importing && !reparsing ? 'pointer' : 'not-allowed',
            fontFamily: 'var(--font-ui)', transition: 'background 0.15s',
          }}
        >
          {importing
            ? csv.importing
            : resolvedTargetTitle
              ? csv.importIntoDeck(resolvedTargetTitle)
              : csv.importBtn}
        </button>
      </div>
    </div>
  )
}

// ── Result screen ─────────────────────────────────────────────────────────────

function ResultScreen({
  result, onClose, onImportMore,
}: {
  result: ImportResult
  onClose: () => void
  onImportMore: () => void
}) {
  const { tr } = useLocale()
  const csv = tr.csvImport
  const problems = result.problems ?? []
  const legacyErrors = result.errors ?? []
  const lines = problems.length > 0
    ? problems.map(p => `Row ${p.row}: ${p.reason}`)
    : legacyErrors
  const hasProblems = lines.length > 0

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 44, marginBottom: 16 }}>{hasProblems ? '⚠️' : '✅'}</div>
      <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
        {csv.importCompleteTitle}
      </h3>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--text-secondary)' }}>
        {csv.deckUpdated}
      </p>

      <div style={{ display: 'flex', gap: 32, justifyContent: 'center', marginBottom: 20 }}>
        <Stat label={csv.importedLabel} value={result.imported} color="var(--success)" />
        <Stat label={csv.skippedLabel}  value={result.skipped}  color="var(--warning)" />
      </div>

      {hasProblems && (
        <div style={{ textAlign: 'left', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: 20, maxHeight: 160, overflowY: 'auto' }}>
          <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {csv.errorsLabel(lines.length)}
          </p>
          {lines.map((line, i) => (
            <p key={i} style={{ margin: '0 0 4px', fontSize: 12, color: 'var(--danger)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {line}
            </p>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button onClick={onImportMore} style={cancelBtnStyle}>{csv.importAnother}</button>
        <button
          onClick={onClose}
          style={{ padding: '10px 24px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}
        >
          {csv.doneBtn}
        </button>
      </div>
    </div>
  )
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function StepDot({ active, done }: { active?: boolean; done?: boolean }) {
  return (
    <div style={{
      width: 8, height: 8, borderRadius: '50%',
      background: done ? 'var(--accent)' : active ? 'var(--accent)' : 'var(--border)',
      opacity: done ? 1 : active ? 1 : 0.4,
    }} />
  )
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <div style={{ fontSize: 32, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{label}</div>
    </div>
  )
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700,
  color: 'var(--text-secondary)', textTransform: 'uppercase',
  letterSpacing: '0.06em', marginBottom: 8,
}

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', fontSize: 14,
  background: 'var(--bg-secondary)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
  outline: 'none', cursor: 'pointer', fontFamily: 'var(--font-ui)',
  appearance: 'auto',
}

const cancelBtnStyle: React.CSSProperties = {
  padding: '10px 20px', background: 'var(--bg-secondary)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)',
  fontSize: 14, cursor: 'pointer', color: 'var(--text-primary)',
  fontFamily: 'var(--font-ui)',
}

// Exported for tests — the single derivation the banner, table and action share.
// eslint-disable-next-line react-refresh/only-export-components -- test-only exports; moving them would split the derivation from the component that owns it
export { validate, initialAssignments, toColumnMapping }
export type { ColumnAssignment, ColumnRole, ParsePreview }
